import { HrefLink as Link } from "@/components/ui/href-link";
import { RequirePermission } from "@/hooks/shared/CheckAuth";
import { createFileRoute } from "@tanstack/react-router";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Plus } from "lucide-react";

import { EmptyState } from "@/components/dashboard/data-table/empty-state";
import { FilterBar } from "@/components/dashboard/data-table/filter-bar";
import { Pagination } from "@/components/dashboard/data-table/pagination";
import { SortHeader } from "@/components/dashboard/data-table/sort-header";
import {
  TBody,
  TD,
  TEmpty,
  TH,
  THead,
  TR,
  Table,
  TableShell,
} from "@/components/dashboard/data-table/table";
import { MapPanel } from "@/components/dashboard/map/map-panel";
import { MapSurface } from "@/components/dashboard/map/map-surface";
import { PageHeader } from "@/components/dashboard/page-header";
import { QueryState } from "@/components/dashboard/query-state";
import { StatusPill } from "@/components/dashboard/status-pill";
import { ZoneRowActions } from "@/components/zones/row-actions";
import { nav, zones as copy } from "@/content";
import { can } from "@/constants/permissions";
import { ringAreaSquareMetres } from "@/utils/geo";
import { readEnum, readPage, readPageSize, readSearch, readSort, emptyPage } from "@/utils/query-params";
import { useQueryParams } from "@/hooks/shared/useQueryParams";
import { useFmt } from "@/hooks/shared/useFmt";
import { useDocumentTitle } from "@/hooks/shared/useDocumentTitle";
import { useSession } from "@/context/UserContext";
import {
  GEOFENCE_POLICIES,
  ZONE_SORT_COLUMNS,
  ZONE_STATES,
  listZones,
} from "@/api/zones";

const BASE_PATH = "/dashboard/zones";

/**
 * The zones list, with every boundary on one plan above it.
 *
 * Supervisors reach this page read-only. The prototype made `/zones` admin-only
 * and drew no polygons on the tracking map, so a supervisor reading "outside any
 * zone" against a guard had no way to find out where the zone actually was — the
 * data existed and was reachable by any authenticated user through
 * `GET /api/zones`, and nothing consumed it.
 */
export const Route = createFileRoute("/_main/dashboard/zones/")({
  component: () => (
    <RequirePermission action="zones.read">
      <ZonesPage />
    </RequirePermission>
  ),
});

function ZonesPage() {
  const { user, settings } = useSession();
  useDocumentTitle(copy.title);
  const params = useQueryParams();
  const fmt = useFmt();

  const sort = readSort(params, ZONE_SORT_COLUMNS, "name");
  const state = readEnum(params, "state", ZONE_STATES) ?? "live";

  const query = {
    search: readSearch(params),
    policy: readEnum(params, "policy", GEOFENCE_POLICIES),
    state,
    sort,
    page: readPage(params),
    pageSize: readPageSize(params),
  };

  const list = useQuery({
    queryKey: ["zones", "list", query],
    queryFn: () => listZones(query),
    placeholderData: keepPreviousData,
  });
  /* Never invent an empty page while the read is still open. `emptyPage()` was
     the intermittent "لا توجد مناطق بعد" flash: first paint had no cache, so the
     table rendered the genuine-empty copy before the response landed. */
  const result = list.data ?? emptyPage<never>();
  const awaitingData = !list.data && !list.isError;

  /* No request for the map centre. It is an org setting and it already arrived
     with the session — the server-rendered version called for it because it had
     no session object to hand. */
  const centre = { lat: settings.defaultMapLat, lng: settings.defaultMapLng };

  const canWrite = can(user.role, "zones.write");
  const canArchive = can(user.role, "zones.archive");
  const isFiltered = Boolean(readSearch(params) || params.policy);

  const showActions = canWrite || canArchive;
  const columnCount = 6 + (showActions ? 1 : 0);

  return (
    <div className="mx-auto w-full max-w-shell px-6 py-8">
      <PageHeader
        eyebrow={nav.groups.operations}
        title={copy.title}
        lead={copy.lead}
        actions={
          canWrite ? (
            <Link
              to={`${BASE_PATH}/new`}
              className="btn-primary inline-flex h-10 items-center gap-2 rounded-lg px-4 text-sm font-bold transition-transform duration-300 ease-brand"
            >
              <Plus aria-hidden className="size-4" strokeWidth={1.6} />
              {copy.newTitle}
            </Link>
          ) : null
        }
      />

      {/* Said plainly rather than left to be inferred from a missing button. A
          supervisor who cannot find "add zone" should know why. */}
      {!canWrite ? <p className="mt-5 text-xs text-n6">{copy.readOnlyNote}</p> : null}

      <div className="mt-6">
        <FilterBar
          params={params}
          basePath={BASE_PATH}
          searchPlaceholder={copy.searchPlaceholder}
          selects={[
            {
              key: "policy",
              label: copy.filterPolicy,
              value: typeof params.policy === "string" ? params.policy : "",
              options: [
                { value: "block", label: copy.policyBlock },
                { value: "flag", label: copy.policyFlag },
              ],
            },
            {
              key: "state",
              label: copy.filterState,
              value: state,
              includeAll: false,
              options: [
                { value: "live", label: copy.stateLive },
                { value: "archived", label: copy.stateArchived },
              ],
            },
          ]}
        />
      </div>

      <QueryState
        isPending={list.isPending || awaitingData}
        isError={list.isError && !list.data}
        error={list.error}
        onRetry={() => {
          void list.refetch();
        }}
      >
        {/* Only when there is geometry to draw. An empty instrument panel above an
            empty table is two ways of saying the same nothing. */}
        {list.isSuccess && result.rows.length > 0 ? (
          <MapPanel title={copy.planTitle} className="mt-6">
            <MapSurface
              zones={result.rows.map((row) => ({
                id: row.id,
                name: row.name,
                color: row.color,
                polygon: row.polygon,
                archived: row.archivedAt !== null,
              }))}
              centre={centre}
              numerals={user.numeralSystem}
            />
          </MapPanel>
        ) : null}

        <TableShell className="mt-5" busy={list.isPlaceholderData || list.isFetching}>
          <Table>
            <THead>
              <SortHeader
                column="name"
                label={copy.columnName}
                params={params}
                basePath={BASE_PATH}
                current={sort}
              />
              <TH>{copy.columnPolicy}</TH>
              <SortHeader
                column="guards"
                label={copy.columnGuards}
                params={params}
                basePath={BASE_PATH}
                current={sort}
                numeric
              />
              <SortHeader
                column="required"
                label={copy.columnRequired}
                params={params}
                basePath={BASE_PATH}
                current={sort}
                numeric
              />
              <TH numeric>{copy.columnArea}</TH>
              <SortHeader
                column="createdAt"
                label={copy.columnCreated}
                params={params}
                basePath={BASE_PATH}
                current={sort}
              />
              {showActions ? <TH srOnly>{copy.actionsLabel}</TH> : null}
            </THead>

            <TBody>
              {list.isSuccess && result.rows.length === 0 ? (
                <TEmpty colSpan={columnCount}>
                  <EmptyState
                    variant={isFiltered ? "filtered" : "empty"}
                    title={
                      isFiltered
                        ? copy.emptyFilteredTitle
                        : state === "archived"
                          ? copy.emptyArchivedTitle
                          : copy.emptyTitle
                    }
                    lead={
                      isFiltered
                        ? copy.emptyFilteredLead
                        : state === "archived"
                          ? copy.emptyArchivedLead
                          : copy.emptyLead
                    }
                    actionHref={
                      isFiltered
                        ? BASE_PATH
                        : canWrite && state === "live"
                          ? `${BASE_PATH}/new`
                          : undefined
                    }
                    actionLabel={
                      isFiltered
                        ? copy.clearFilters
                        : canWrite && state === "live"
                          ? copy.newTitle
                          : undefined
                    }
                  />
                </TEmpty>
              ) : (
                result.rows.map((row) => (
                  <TR key={row.id} muted={row.archivedAt !== null}>
                    <TD>
                      <span className="flex items-center gap-2.5">
                        {/* The colour is a shorthand for the plan above, never the
                            only way to identify a zone — the name is always beside it. */}
                        <span
                          aria-hidden
                          className="size-2.5 shrink-0 rounded-sm"
                          style={{ backgroundColor: row.color }}
                        />
                        <Link
                          to={`${BASE_PATH}/${row.id}`}
                          className="font-semibold text-n9 transition-colors duration-200 ease-brand hover:text-orange-700"
                        >
                          {row.name}
                        </Link>
                        {row.archivedAt ? (
                          <StatusPill tone="muted">{copy.archivedBadge}</StatusPill>
                        ) : null}
                      </span>
                    </TD>

                    <TD>
                      {/* `flag` is the state that needs watching — it means punches
                          from outside the boundary are accepted. */}
                      {row.geofencePolicy === "flag" ? (
                        <StatusPill tone="attention">{copy.policyFlag}</StatusPill>
                      ) : (
                        <StatusPill tone="neutral">{copy.policyBlock}</StatusPill>
                      )}
                    </TD>

                    <TD numeric>
                      <span
                        className={
                          row.guardCount < row.requiredGuards && !row.archivedAt
                            ? "font-bold text-orange-700"
                            : undefined
                        }
                        title={
                          row.guardCount < row.requiredGuards && !row.archivedAt
                            ? copy.understaffed
                            : undefined
                        }
                      >
                        {fmt.count(row.guardCount)}
                      </span>
                    </TD>

                    <TD numeric>{fmt.count(row.requiredGuards)}</TD>

                    <TD numeric>
                      <span className="text-xs">{fmt.area(ringAreaSquareMetres(row.polygon))}</span>
                    </TD>

                    <TD numeric>
                      <span className="text-xs">{fmt.dateNumeric(row.createdAt)}</span>
                    </TD>

                    {showActions ? (
                      <TD className="text-end">
                        <ZoneRowActions
                          id={row.id}
                          name={row.name}
                          archived={row.archivedAt !== null}
                          memberCount={row.guardCount}
                          unfencedIfArchived={row.unfencedIfArchived}
                          numerals={user.numeralSystem}
                          canWrite={canWrite}
                          canArchive={canArchive}
                        />
                      </TD>
                    ) : null}
                  </TR>
                ))
              )}
            </TBody>
          </Table>

          {result.rows.length > 0 ? (
            <Pagination
              page={result.page}
              pageCount={result.pageCount}
              pageSize={result.pageSize}
              total={result.total}
              params={params}
              basePath={BASE_PATH}
              numerals={user.numeralSystem}
            />
          ) : null}
        </TableShell>
      </QueryState>
    </div>
  );
}
